import { useState, useEffect, useCallback } from "react";
import EventEmitter from "events";
import { Services } from "@/types/serviceTypes";
import { emitServices } from "@/utils/services/emitServices.ts";

const eventBus = new EventEmitter();

export function useSharedServices(
  initialState: Services,
): [
  Services,
  (update: Services | ((prevState: Services) => Services)) => void,
] {
  const [services, setServices] = useState(initialState);

  useEffect(() => {
    setServices((prev) => {
      let changed = false;

      const next = prev.map((service) => {
        if (service.isMandatory && !service.selected) {
          changed = true;
          return { ...service, selected: true };
        }
        return service;
      });

      if (changed) {
        emitServices(next.filter((service) => service.selected));
      }

      return changed ? next : prev;
    });
  }, [services, setServices]);

  useEffect(() => {
    const onPageShow = () => {
      emitServices(services.filter((s) => s.selected));
    };

    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, [services]);

  useEffect(() => {
    const handleUpdate = (
      update: Services | ((prevState: Services) => Services),
    ) => {
      if (typeof update === "function") {
        setServices((prevState) => update(prevState));
      } else {
        setServices(update);
      }
    };

    eventBus.on("updateServices", handleUpdate);

    return () => {
      eventBus.removeListener("updateServices", handleUpdate);
    };
  }, []);

  const updateServices = useCallback(
    (update: Services | ((prevState: Services) => Services)) => {
      eventBus.emit("updateServices", update);
    },
    [],
  );

  return [services, updateServices];
}
